/**
 * Filing Hooks
 *
 * React Query hooks for filing history operations.
 * Provides query hooks for listing/fetching and mutation hooks for updates.
 *
 * Session 3.2: Filing History Frontend
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { filingApi } from '@/services/api/filingApi';
import type { FilingListParams } from '@/types/filing';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const filingKeys = {
  all: ['filings'] as const,
  lists: () => [...filingKeys.all, 'list'] as const,
  list: (params: FilingListParams) => [...filingKeys.lists(), params] as const,
  details: () => [...filingKeys.all, 'detail'] as const,
  detail: (id: string) => [...filingKeys.details(), id] as const,
  years: () => [...filingKeys.all, 'years'] as const,
  stats: () => [...filingKeys.all, 'stats'] as const,
};

// =============================================================================
// LIST QUERIES
// =============================================================================

/**
 * Hook for fetching paginated filing list.
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useFilings({ year: 2025, page: 1 });
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <ErrorMessage error={error} />;
 *
 * return (
 *   <FilingList
 *     filings={data.filings}
 *     pagination={data.pagination}
 *   />
 * );
 * ```
 */
export function useFilings(params: FilingListParams = {}) {
  return useQuery({
    queryKey: filingKeys.list(params),
    queryFn: () => filingApi.list(params),
  });
}

/**
 * Hook for fetching years with filings.
 * Used for the year filter dropdown.
 *
 * @example
 * ```tsx
 * const { data: years } = useFilingYears();
 *
 * return (
 *   <select>
 *     <option value="">All Years</option>
 *     {years?.map(year => (
 *       <option key={year} value={year}>{year}</option>
 *     ))}
 *   </select>
 * );
 * ```
 */
export function useFilingYears() {
  return useQuery({
    queryKey: filingKeys.years(),
    queryFn: () => filingApi.getYears(),
    staleTime: 5 * 60 * 1000, // Years don't change often, cache for 5 minutes
  });
}

/**
 * Hook for fetching filing statistics.
 *
 * @example
 * ```tsx
 * const { data: stats } = useFilingStats();
 *
 * return (
 *   <div>
 *     <StatCard title="Total Filings" value={stats?.total_filings} />
 *     <StatCard title="Filed" value={stats?.filed_count} />
 *   </div>
 * );
 * ```
 */
export function useFilingStats() {
  return useQuery({
    queryKey: filingKeys.stats(),
    queryFn: () => filingApi.getStats(),
  });
}

// =============================================================================
// DETAIL QUERY
// =============================================================================

/**
 * Hook for fetching filing detail with employees.
 *
 * @param id - Filing UUID (null to disable query)
 *
 * @example
 * ```tsx
 * const { data: filing, isLoading } = useFilingDetail(selectedId);
 *
 * if (!selectedId) return null;
 *
 * return (
 *   <FilingDetailModal
 *     filing={filing}
 *     isLoading={isLoading}
 *     onClose={() => setSelectedId(null)}
 *   />
 * );
 * ```
 */
export function useFilingDetail(id: string | null) {
  return useQuery({
    queryKey: filingKeys.detail(id ?? ''),
    queryFn: () => filingApi.getDetail(id!),
    enabled: !!id,
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

/**
 * Hook for downloading a C10 file.
 *
 * @example
 * ```tsx
 * const { mutate: download, isPending } = useDownloadC10();
 *
 * <Button
 *   onClick={() => download({ id: filing.id, filename: 'C10_Jan_2025.txt' })}
 *   loading={isPending}
 * >
 *   Download C10
 * </Button>
 * ```
 */
export function useDownloadC10() {
  return useMutation({
    mutationFn: ({ id, filename }: { id: string; filename: string }) =>
      filingApi.download(id, filename),
  });
}

/**
 * Hook for marking a filing as filed.
 * Automatically invalidates filing queries on success.
 *
 * @example
 * ```tsx
 * const { mutate: markFiled, isPending } = useMarkAsFiled();
 *
 * <Button
 *   onClick={() => markFiled({ id: filing.id })}
 *   loading={isPending}
 * >
 *   Mark as Filed
 * </Button>
 * ```
 */
export function useMarkAsFiled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => filingApi.markAsFiled(id),
    onSuccess: (_data, variables) => {
      // Invalidate list queries to reflect status change
      queryClient.invalidateQueries({ queryKey: filingKeys.lists() });
      // Invalidate the specific detail query
      queryClient.invalidateQueries({
        queryKey: filingKeys.detail(variables.id),
      });
      // Invalidate stats as filed_count changes
      queryClient.invalidateQueries({ queryKey: filingKeys.stats() });
    },
  });
}

/**
 * Hook for updating filing notes.
 * Automatically invalidates the filing detail query on success.
 *
 * @example
 * ```tsx
 * const { mutate: updateNotes, isPending } = useUpdateNotes();
 *
 * const handleSaveNotes = (notes: string) => {
 *   updateNotes({ id: filing.id, notes });
 * };
 * ```
 */
export function useUpdateNotes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      filingApi.updateNotes(id, notes),
    onSuccess: (_data, variables) => {
      // Invalidate the specific detail query to show updated notes
      queryClient.invalidateQueries({
        queryKey: filingKeys.detail(variables.id),
      });
    },
  });
}
